import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CompareMode, Quality, Stroke, Version } from '../types.ts';
import { AnnotationCanvas, type Tool } from './AnnotationCanvas.tsx';

/** Prefer the full-resolution stream; fall back to the proxy when asked or when it is all we have. */
export function mediaUrl(version: Version | null, quality: Quality): string | undefined {
  if (!version) return undefined;
  if (quality === 'proxy') return version.media.proxy ?? version.media.full ?? undefined;
  return version.media.full ?? version.media.proxy ?? undefined;
}

interface Transform {
  zoom: number;
  panX: number;
  panY: number;
}

interface Props {
  primary: Version | null;
  /**
   * The clip that comes after `primary`. It is loaded into the off-screen half of the player
   * while `primary` is still running, which is what turns a clip change into a swap.
   */
  next: Version | null;
  compare: Version | null;
  compareMode: CompareMode;
  wipe: number;
  onWipeChange: (wipe: number) => void;
  /** Shared pan/zoom (pan as a fraction of the frame, zoom as a scale factor). */
  zoom: number;
  panX: number;
  panY: number;
  onTransformChange: (t: Transform) => void;
  quality: Quality;
  primaryRef: React.RefObject<HTMLVideoElement | null>;
  compareRef: React.RefObject<HTMLVideoElement | null>;
  muted: boolean;
  loop: boolean;

  drawEnabled: boolean;
  strokes: Stroke[];
  onStrokesChange: (strokes: Stroke[]) => void;
  overlayStrokes: Stroke[];
  tool: Tool;
  color: string;
  lineWidth: number;
  onStrokeComplete?: (stroke: Stroke) => void;
}

function PlayerStageImpl({
  primary,
  next,
  compare,
  compareMode,
  wipe,
  onWipeChange,
  zoom,
  panX,
  panY,
  onTransformChange,
  quality,
  primaryRef,
  compareRef,
  muted,
  loop,
  drawEnabled,
  strokes,
  onStrokesChange,
  overlayStrokes,
  tool,
  color,
  lineWidth,
  onStrokeComplete,
}: Props): React.JSX.Element {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const panning = useRef<{ x: number; y: number } | null>(null);

  const comparing = compareMode !== 'off' && compare !== null;
  const sideBySide = comparing && compareMode === 'sidebyside';

  // ----------------------------------------------------------------- pan/zoom
  // Everything visible (both videos + the annotation layer) shares this transform, so the
  // comparison and the drawings stay glued to the image at any zoom.
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 8;
  // Memoised because it is handed to both videos and the annotation layer as `style`: a fresh
  // object each render re-applies the styles and defeats the layer's own memoisation.
  const videoTransform = useMemo(
    () => ({
      transform: `translate(${panX * 100}%, ${panY * 100}%) scale(${zoom})`,
      transformOrigin: '0 0' as const,
    }),
    [panX, panY, zoom],
  );

  // rVFC/CSS transforms need the live values inside a non-passive wheel listener.
  const txRef = useRef({ zoom, panX, panY });
  txRef.current = { zoom, panX, panY };

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width;
      const cy = (e.clientY - rect.top) / rect.height;
      const { zoom: z, panX: px, panY: py } = txRef.current;
      const factor = Math.exp(-e.deltaY * 0.0015);
      const z2 = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor));
      if (z2 === z) return;
      // Keep the point under the cursor fixed while zooming.
      const nextPanX = cx - (cx - px) * (z2 / z);
      const nextPanY = cy - (cy - py) * (z2 / z);
      // Back at fit, snap the pan away so the frame recenters cleanly.
      onTransformChange(z2 <= MIN_ZOOM ? { zoom: 1, panX: 0, panY: 0 } : { zoom: z2, panX: nextPanX, panY: nextPanY });
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [onTransformChange]);

  // Drag to pan — only when not drawing and not grabbing the wipe handle.
  useEffect(() => {
    const onMove = (e: PointerEvent): void => {
      const start = panning.current;
      const stage = stageRef.current;
      if (!start || !stage) return;
      const rect = stage.getBoundingClientRect();
      const { zoom: z, panX: px, panY: py } = txRef.current;
      const nextPanX = px + (e.clientX - start.x) / rect.width;
      const nextPanY = py + (e.clientY - start.y) / rect.height;
      panning.current = { x: e.clientX, y: e.clientY };
      onTransformChange({ zoom: z, panX: nextPanX, panY: nextPanY });
    };
    const onUp = (): void => {
      panning.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [onTransformChange]);

  const updateWipeFromEvent = useCallback(
    (clientX: number) => {
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      onWipeChange(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)));
    },
    [onWipeChange],
  );

  // Drag anywhere once the handle is grabbed, including outside the stage.
  useEffect(() => {
    if (compareMode !== 'wipe') return;
    const onMove = (e: PointerEvent): void => {
      if (dragging.current) updateWipeFromEvent(e.clientX);
    };
    const onUp = (): void => {
      dragging.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [compareMode, updateWipeFromEvent]);

  const primarySrc = mediaUrl(primary, quality);
  const nextSrc = mediaUrl(next, quality);
  const compareSrc = mediaUrl(compare, quality);

  // ------------------------------------------------------- double-buffered playback
  /*
   * Two <video> elements that are never unmounted, only swapped. One shows the current clip
   * while the other already holds the next one, decoded and paused on its first frame; a clip
   * change then costs nothing but a flip of `activeSlot`.
   *
   * The element that a single <video key={clipId}> used to give us was torn down and rebuilt on
   * every clip change, and a freshly mounted element has no picture yet — that empty moment,
   * against the stage's black background, was the black frame between clips.
   */
  const slotA = useRef<HTMLVideoElement | null>(null);
  const slotB = useRef<HTMLVideoElement | null>(null);
  const [activeSlot, setActiveSlot] = useState<'a' | 'b'>('a');

  useLayoutEffect(() => {
    const a = slotA.current;
    const b = slotB.current;
    if (!a || !b || !primarySrc) return;

    /*
     * What an element actually holds, asked of the element itself rather than tracked on the
     * side: a clip whose media is not ready yet renders the placeholder instead of the pair, and
     * any bookkeeping we kept across that would claim a src that the remounted element has lost.
     * `.src` reports an absolute URL, so the wanted one is resolved the same way before comparing.
     */
    const holds = (el: HTMLVideoElement, src: string): boolean =>
      el.src !== '' && el.src === new URL(src, location.href).href;

    const elements = { a, b };
    const idle = activeSlot === 'a' ? 'b' : 'a';
    // Prefer whichever element already holds this clip: that is the seamless path. Everything
    // else (a jump down the strip, a quality switch) loads into the active element and looks
    // exactly as it did before — never worse.
    const target =
      holds(elements[activeSlot], primarySrc) ? activeSlot
      : holds(elements[idle], primarySrc) ? idle
      : activeSlot;
    const standby = target === 'a' ? 'b' : 'a';

    if (!holds(elements[target], primarySrc)) elements[target].src = primarySrc;
    // Park the next clip in the element nobody is watching. Skipped when it is the one already
    // on screen, which is what a single-clip review or the last clip of a sequence looks like.
    if (nextSrc && nextSrc !== primarySrc && !holds(elements[standby], nextSrc)) {
      elements[standby].src = nextSrc;
    }

    // The element going off screen must also go quiet. Hiding it (opacity 0) is not stopping it:
    // a clip left playing there — looping, typically — kept running unseen, and its sound
    // played on under the next clip. So the parked element is always paused and muted, and only
    // the one on screen follows the mute button.
    const parked = elements[standby];
    if (!parked.paused) parked.pause();
    parked.muted = true;
    elements[target].muted = muted;

    // Published before any of the passive effects that read it — useFramePlayer, the `ended`
    // listener and the follow loop all bind to whichever element is on screen right now.
    primaryRef.current = elements[target];
    if (target !== activeSlot) setActiveSlot(target);
  }, [primarySrc, nextSrc, activeSlot, primaryRef, muted]);

  // The compare layer is clipped from the right, so dragging the handle rightwards reveals
  // more of it — the behaviour people expect from a making-of wipe.
  const compareClip =
    compareMode === 'wipe' ? { clipPath: `inset(0 ${(1 - wipe) * 100}% 0 0)` } : undefined;

  return (
    <div
      className={`stage${sideBySide ? ' stage--split' : ''}${zoom > 1 ? ' stage--pannable' : ''}`}
      ref={stageRef}
      onPointerDown={(e) => {
        // Pan only once zoomed in — at fit the image must not drift when the stage is dragged.
        if (zoom <= 1) return;
        // The middle button always pans, drawing or not — the hand never has to leave the
        // drawing to move the frame. The left button pans too, unless it is drawing or
        // grabbing the wipe handle.
        if (e.button === 1) {
          e.preventDefault();
          panning.current = { x: e.clientX, y: e.clientY };
          return;
        }
        if (e.button !== 0 || drawEnabled) return;
        if ((e.target as HTMLElement).closest('.stage__wipe-handle')) return;
        panning.current = { x: e.clientX, y: e.clientY };
      }}
      // A middle click would otherwise start the browser's own auto-scroll on some systems.
      onMouseDown={(e) => {
        if (e.button === 1) e.preventDefault();
      }}
      onDoubleClick={() => onTransformChange({ zoom: 1, panX: 0, panY: 0 })}
    >
      <div className="stage__pane">
        {primarySrc ? (
          // No `key` and no `src` here on purpose: both are managed by the layout effect above,
          // because unmounting either element is exactly what we are avoiding.
          <>
            <video
              ref={slotA}
              className={`stage__video${activeSlot === 'a' ? '' : ' stage__video--standby'}`}
              style={videoTransform}
              muted={muted || activeSlot !== 'a'}
              playsInline
              preload="auto"
              loop={loop}
            />
            <video
              ref={slotB}
              className={`stage__video${activeSlot === 'b' ? '' : ' stage__video--standby'}`}
              style={videoTransform}
              muted={muted || activeSlot !== 'b'}
              playsInline
              preload="auto"
              loop={loop}
            />
          </>
        ) : (
          <div className="stage__empty">
            {primary ? `${primary.ingestState}…` : 'No clip selected'}
          </div>
        )}
        {sideBySide && primary ? <span className="stage__label">{primary.label}</span> : null}
      </div>

      {comparing && compareSrc ? (
        <div
          className={`stage__pane stage__pane--compare stage__pane--${compareMode}`}
          style={compareClip}
        >
          <video
            key={`${compare?.id}-${quality}-cmp`}
            ref={compareRef}
            className="stage__video"
            style={videoTransform}
            src={compareSrc}
            muted
            playsInline
            preload="auto"
          />
          {sideBySide && compare ? <span className="stage__label">{compare.label}</span> : null}
        </div>
      ) : null}

      {compareMode === 'wipe' && comparing ? (
        <>
          <div
            className="stage__wipe-handle"
            style={{ left: `${wipe * 100}%` }}
            onPointerDown={(e) => {
              dragging.current = true;
              updateWipeFromEvent(e.clientX);
            }}
            role="separator"
            aria-label="Wipe-Position"
          >
            <span className="stage__wipe-grip" />
          </div>
          <span className="stage__wipe-label stage__wipe-label--left">{compare?.label}</span>
          <span className="stage__wipe-label stage__wipe-label--right">{primary?.label}</span>
        </>
      ) : null}

      <AnnotationCanvas
        strokes={strokes}
        onStrokesChange={onStrokesChange}
        overlayStrokes={overlayStrokes}
        enabled={drawEnabled}
        tool={tool}
        color={color}
        lineWidth={lineWidth}
        onStrokeComplete={onStrokeComplete}
        style={videoTransform}
        aspect={primary?.width && primary.height ? primary.width / primary.height : null}
        leftHalf={sideBySide}
      />
    </div>
  );
}

/**
 * Holds the videos and the annotation layer. Its props are deliberately stable (see the
 * stroke memos in ReviewPage), so during playback this whole subtree is skipped.
 */
export const PlayerStage = memo(PlayerStageImpl);
